import { Component } from '@theme/component';
import { formatShopMoney } from '@theme/money-formatting';
import { yieldToMainThread } from '@theme/utilities';

/**
 * Custom element for the _swatches-custom block.
 */
export class NaSwatchesCustomComponent extends Component {
    #abortController = new AbortController();
    #selectionId = 0;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(
            'change',
            (event) => {
                const input = event.target;
                if (!(input instanceof HTMLInputElement) || !input.classList.contains('na-form__input')) return;
                if (!input.checked) return;
                const swatch = input.labels?.[0] ?? this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
                if (swatch) this.#selectSwatch(swatch);
            },
            { signal: this.#abortController.signal }
        );
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    /**
     * @param {Event} event
     */
    selectSwatch(event) {
        event.preventDefault();
        const swatch = event.target instanceof Element ? event.target.closest('.customize-product__swatch') : null;
        if (swatch) this.#selectSwatch(swatch);
    }

    /**
     * @param {Element} swatch
     */
    async #selectSwatch(swatch) {
        const card = this.closest('product-card') ?? this.closest('[data-product-id]');
        if (!card || !(swatch instanceof HTMLElement)) return;

        this.querySelectorAll('.customize-product__swatch').forEach((s) => s.classList.remove('selectedColor'));
        swatch.classList.add('selectedColor');

        const variantText = card.querySelector('.product-form__variant-active p');
        if (variantText) variantText.textContent = swatch.dataset.name ?? '';

        const imageUrl = swatch.dataset.imageChange;
        if (imageUrl) {
            const imageEl = card.querySelector('.customize-product__image .na-product-card__img--normal-state');
            if (imageEl instanceof HTMLImageElement) {
                imageEl.src = imageUrl;
                imageEl.srcset = `${imageUrl} 1x`;
                imageEl.removeAttribute('data-src');
                imageEl.removeAttribute('data-srcset');
            }
        }

        this.#syncHoverImage(card, swatch.dataset.hoverImageChange ?? '');

        const price = parseInt(swatch.dataset.price || '0');
        const compareAtPrice = parseInt(swatch.dataset.compareAtPrice || '0');
        const discount = parseInt(swatch.dataset.discount || '0');
        const priceWrapper = card.querySelector('product-price');
        const priceEl = priceWrapper?.querySelector('.price');
        const comparePriceEl = priceWrapper?.querySelector('.compare-at-price');

        if (priceEl) {
            let finalPrice = price;

            if (discount > 0 || compareAtPrice > price) {
                let finalComparePrice = compareAtPrice;

                if (discount > 0) {
                    finalPrice = Math.max(0, price - discount * 100);
                    if (!compareAtPrice) finalComparePrice = price;
                }

                priceEl.textContent = formatShopMoney(finalPrice);

                if (comparePriceEl instanceof HTMLElement) {
                    comparePriceEl.textContent = formatShopMoney(finalComparePrice);
                    comparePriceEl.style.display = '';
                } else {
                    const priceContainer = card.querySelector('.price-nurtureand');
                    if (priceContainer) {
                        const newCompareEl = document.createElement('span');
                        newCompareEl.className = 'compare-at-price';
                        priceContainer.insertBefore(newCompareEl, priceEl);
                        newCompareEl.textContent = formatShopMoney(finalComparePrice);
                        newCompareEl.style.display = '';
                    }
                }
            } else {
                priceEl.textContent = formatShopMoney(price);
                if (comparePriceEl instanceof HTMLElement) comparePriceEl.style.display = 'none';
            }
        }

        const giftBadge = card.querySelector('.na-free-gift-badge[data-free-gift-variants]');
        if (giftBadge instanceof HTMLElement && swatch.dataset.varid) {
            giftBadge.hidden = !giftBadge.dataset.freeGiftVariants?.includes(`,${swatch.dataset.varid},`);
        }

        const discountBadge = card.querySelector('.na-product__discount-badge');
        let finalDiscount = 0;
        if (discount > 0) {
            finalDiscount = discount * 100;
        } else if (compareAtPrice > price) {
            finalDiscount = compareAtPrice - price;
        }
        if (discountBadge instanceof HTMLElement) {
            if (finalDiscount > 0) {
                discountBadge.style.display = '';
                discountBadge.textContent = `${formatShopMoney(finalDiscount)} OFF`;
                discountBadge.classList.remove('hidden');
            } else {
                discountBadge.style.display = 'none';
                discountBadge.classList.add('hidden');
            }
        }

        const selectionId = ++this.#selectionId;
        await yieldToMainThread();
        if (selectionId !== this.#selectionId || !this.isConnected) return;

        this.#filterShowcaseGallery(card, swatch.dataset.name ?? '');

        // If the card hosts a product form (e.g. the product showcase), keep its
        // variant id in sync so "Add to cart" submits the selected colour.
        const selectedVariantId = swatch.dataset.varid;
        if (selectedVariantId) {
            card.querySelectorAll(
                'product-form-component input[name="id"], product-form-component input[ref="variantId"]'
            ).forEach((input) => {
                if (input instanceof HTMLInputElement) {
                    input.value = selectedVariantId;
                    input.setAttribute('value', selectedVariantId);
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            });
        }

        const rawIconData = swatch.dataset.iconWithText;
        if (rawIconData) {
            try {
                this.#updateVariantIcons(card, JSON.parse(rawIconData));
            } catch {
                // malformed JSON — leave features as-is
            }
        }
    }

    /**
     * @param {Element} card
     * @param {string} hoverImageUrl
     */
    #syncHoverImage(card, hoverImageUrl) {
        const imageWrap = card.querySelector('.customize-product__image');
        if (!imageWrap) return;

        const hoverImageEl = imageWrap.querySelector('.na-product-card__img--hover-state');

        if (!hoverImageUrl) {
            const hoverPicture = hoverImageEl?.closest('picture') ?? hoverImageEl;
            hoverPicture?.remove();
            return;
        }

        if (hoverImageEl instanceof HTMLImageElement) {
            hoverImageEl.src = hoverImageUrl;
            hoverImageEl.srcset = `${hoverImageUrl} 1x`;
            hoverImageEl.removeAttribute('data-src');
            hoverImageEl.removeAttribute('data-srcset');
            return;
        }

        const picture = document.createElement('picture');
        picture.className = 'na-product-card__hover-picture';

        const source = document.createElement('source');
        source.media = '(hover: none)';
        source.srcset = 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA=';

        const img = document.createElement('img');
        img.className = 'na-product-card__img--hover-state';
        img.src = hoverImageUrl;
        img.srcset = `${hoverImageUrl} 1x`;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.loading = 'lazy';

        picture.append(source, img);

        const normalImage = imageWrap.querySelector('.na-product-card__img--normal-state');
        if (normalImage) {
            normalImage.insertAdjacentElement('afterend', picture);
        } else {
            imageWrap.append(picture);
        }
    }

    /**
     * Filters showcase card-gallery slides/dots by color alt tag (PDP parity).
     * No-ops when the card has no color-tagged gallery (collection cards).
     *
     * @param {Element} card
     * @param {string} colorName
     */
    #filterShowcaseGallery(card, colorName) {
        const gallery = card.querySelector('.card-gallery');
        if (!gallery?.querySelector('[data-color-tag]')) return;

        const colorTag = colorName.trim().toLowerCase().replace(/\s+/g, '_');

        /**
         * @param {Element} el
         * @returns {boolean}
         */
        const shouldShow = (el) => {
            const tag = el.getAttribute('data-color-tag');
            return !colorTag || !tag || tag === colorTag;
        };

        gallery.querySelectorAll('slideshow-slide[data-color-tag]').forEach((slide) => {
            if (shouldShow(slide)) {
                slide.removeAttribute('hidden');
            } else {
                slide.setAttribute('hidden', '');
            }
        });

        gallery.querySelectorAll('li[data-color-tag]').forEach((li) => {
            if (shouldShow(li)) {
                li.removeAttribute('hidden');
            } else {
                li.setAttribute('hidden', '');
            }
        });

        const slideshow = /** @type {any} */ (gallery.querySelector('slideshow-component'));
        if (slideshow?.select) {
            requestAnimationFrame(() => {
                slideshow.select(0, undefined, { animate: false, force: true });
            });
        }
    }

    /**
     * @param {Element} card
     * @param {{ icon: string, text: string }[]} icons
     */
    #updateVariantIcons(card, icons) {
        const featuresEl = card.querySelector('.na-product-card__features');
        if (!featuresEl) return;

        if (!icons.length) {
            featuresEl.hidden = true;
            return;
        }

        featuresEl.hidden = false;
        featuresEl.innerHTML = '';

        icons.forEach(({ icon, text }) => {
            const item = document.createElement('div');
            item.className = 'icon-with-text na-product-card__features-item';

            const img = document.createElement('img');
            img.src = icon;
            img.alt = text;
            img.className = 'na-img';
            img.width = 16;
            img.height = 16;
            img.loading = 'lazy';

            const p = document.createElement('p');
            p.textContent = text;

            item.append(img, p);
            featuresEl.appendChild(item);
        });
    }
}

if (!customElements.get('na-swatches-custom-component')) {
    customElements.define('na-swatches-custom-component', NaSwatchesCustomComponent);
}
